import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../api";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, APT_STATUS } from "../helpers";
import { Plus, Pencil, Archive, RotateCcw, Wifi, KeyRound, Users, BedDouble, Bath, MapPin, Building2, ArrowRight, Copy } from "lucide-react";

const empty = { code: "", name: "", address: "", zone: "", max_guests: 2, bedrooms: 1, bathrooms: 1, wifi_name: "", wifi_password: "", keybox_code: "", cin: "", cir: "", latitude: "", longitude: "", tally_documents_url: "", welcome_guide_url: "", video_url: "", notes: "", image_url: "", owner_id: "", status: "attivo", documents: [] };
const STATUS_LABEL = { attivo: "active", in_acquisizione: "acquiring", sospeso: "suspended", terminato: "terminated", archivio: "archived" };

export default function Apartments() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [owners, setOwners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/apartments").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    if ((user?.sections || []).includes("owners")) {
      api.get("/owners").then((r) => setOwners(r.data)).catch(() => setOwners([]));
    }
  }, [user]);

  // Auto-open edit dialog when arriving with ?edit=<id> (e.g. after duplication)
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && items.length) {
      const it = items.find((a) => a.id === editId);
      if (it) { setForm(it); setOpen(true); setSearchParams({}, { replace: true }); }
    }
  }, [searchParams, items, setSearchParams]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(empty); setOpen(true); };
  const openEdit = (it) => { setForm(it); setOpen(true); };

  const duplicate = async (id) => {
    try {
      const r = await api.post(`/apartments/${id}/duplicate`);
      toast.success("Immobile duplicato · modifica i dati specifici");
      await load();
      setForm(r.data); setOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nella duplicazione"); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("name")); return; }
    try {
      if (form.id) await api.put(`/apartments/${form.id}`, form);
      else await api.post("/apartments", form);
      toast.success(t("saved")); setOpen(false); load();
    } catch { toast.error("Error"); }
  };

  const archive = async (id) => {
    if (!window.confirm(t("archiveConfirm"))) return;
    await api.delete(`/apartments/${id}`); toast.success(t("archived")); load();
  };
  const reactivate = async (a) => {
    await api.put(`/apartments/${a.id}`, { ...a, status: "attivo" }); toast.success(t("saved")); load();
  };

  const shown = filter === "all" ? items : items.filter((a) => a.status === filter);
  const isAdmin = ["super_admin", "admin", "amministratore"].includes(user?.role);

  return (
    <div className="space-y-6 fade-in" data-testid="apartments-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="w-56">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="apt-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatuses")}</SelectItem>
              {APT_STATUS.map((s) => <SelectItem key={s} value={s}>{t(STATUS_LABEL[s])}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="add-apartment-btn" onClick={openNew} className="gap-2 bg-primary hover:bg-primary/90"><Plus size={18} /> {t("add")}</Button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {shown.map((a) => (
          <Card key={a.id} data-testid={`apartment-card-${a.id}`} className={`overflow-hidden border-slate-200 transition-all hover:-translate-y-1 hover:shadow-md ${a.status === "archivio" ? "opacity-70" : ""}`}>
            <div className="relative h-40 w-full bg-gradient-to-br from-slate-800 to-slate-900" style={a.image_url ? { backgroundImage: `url(${a.image_url})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
              {!a.image_url && <div className="flex h-full w-full items-center justify-center text-slate-500"><Building2 size={40} /></div>}
              {a.code && <span data-testid={`apt-code-${a.id}`} className="absolute left-3 top-3 rounded-md bg-slate-900/80 px-2 py-0.5 font-mono text-xs font-semibold text-white">{a.code}</span>}
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-head text-lg font-semibold text-slate-900">{a.name}</h3>
                  <p className="flex items-center gap-1 text-xs text-slate-500"><MapPin size={12} /> {a.zone || a.address || "—"}</p>
                </div>
                <StatusBadge value={a.status} />
              </div>
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-600">
                <span className="flex items-center gap-1"><Users size={15} /> {a.max_guests}</span>
                <span className="flex items-center gap-1"><BedDouble size={15} /> {a.bedrooms}</span>
                <span className="flex items-center gap-1"><Bath size={15} /> {a.bathrooms}</span>
              </div>
              <div className="mt-4 space-y-1.5 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                <div className="flex items-center gap-2"><Wifi size={13} /> {a.wifi_name || "—"} · <span className="font-mono">{a.wifi_password}</span></div>
                <div className="flex items-center gap-2"><KeyRound size={13} /> Keybox: <span className="font-mono font-semibold">{a.keybox_code || "—"}</span></div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button data-testid={`detail-apartment-${a.id}`} size="sm" className="flex-1 gap-1 bg-primary hover:bg-primary/90" onClick={() => navigate(`/apartments/${a.id}`)}>{t("viewDetail")} <ArrowRight size={14} /></Button>
                <Button data-testid={`edit-apartment-${a.id}`} variant="outline" size="sm" className="gap-1" onClick={() => openEdit(a)}><Pencil size={14} /></Button>
                {isAdmin && a.status !== "archivio" && (
                  <Button data-testid={`duplicate-apartment-${a.id}`} variant="outline" size="sm" className="gap-1 text-indigo-600" title={t("duplicate")} onClick={() => duplicate(a.id)}><Copy size={14} /></Button>
                )}
                {isAdmin && (a.status === "archivio"
                  ? <Button data-testid={`reactivate-apartment-${a.id}`} variant="outline" size="sm" className="text-emerald-600" onClick={() => reactivate(a)}><RotateCcw size={14} /></Button>
                  : <Button data-testid={`archive-apartment-${a.id}`} variant="outline" size="sm" className="text-orange-600" onClick={() => archive(a.id)}><Archive size={14} /></Button>)}
              </div>
            </div>
          </Card>
        ))}
        {shown.length === 0 && <p className="text-sm text-slate-400">{t("noData")}</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>{form.id ? t("edit") : t("add")} · {t("apartments")}</DialogTitle><DialogDescription className="sr-only">Form</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("code")}><Input data-testid="apt-code" value={form.code} onChange={(e) => set("code", e.target.value)} placeholder="UDH017" /></Field>
            <Field label={t("status")}>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="apt-status"><SelectValue /></SelectTrigger>
                <SelectContent>{APT_STATUS.map((s) => <SelectItem key={s} value={s}>{t(STATUS_LABEL[s])}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("name")} className="col-span-2"><Input data-testid="apt-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label={t("address")} className="col-span-2"><Input data-testid="apt-address" value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
            <Field label={t("zone")} className="col-span-2"><Input value={form.zone} onChange={(e) => set("zone", e.target.value)} /></Field>
            <Field label={t("guests")}><Input type="number" value={form.max_guests} onChange={(e) => set("max_guests", +e.target.value)} /></Field>
            <Field label={t("bedrooms")}><Input type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", +e.target.value)} /></Field>
            <Field label={t("bathrooms")}><Input type="number" value={form.bathrooms} onChange={(e) => set("bathrooms", +e.target.value)} /></Field>
            <Field label={t("wifi")}><Input value={form.wifi_name} onChange={(e) => set("wifi_name", e.target.value)} /></Field>
            <Field label={t("wifiPassword")}><Input value={form.wifi_password} onChange={(e) => set("wifi_password", e.target.value)} /></Field>
            <Field label={t("keybox")}><Input value={form.keybox_code} onChange={(e) => set("keybox_code", e.target.value)} /></Field>
            <Field label="CIN"><Input data-testid="apt-cin" value={form.cin || ""} onChange={(e) => set("cin", e.target.value)} placeholder="IT..." /></Field>
            <Field label="CIR"><Input data-testid="apt-cir" value={form.cir || ""} onChange={(e) => set("cir", e.target.value)} /></Field>
            <Field label="Latitudine"><Input data-testid="apt-lat" value={form.latitude || ""} onChange={(e) => set("latitude", e.target.value)} placeholder="45.4642" /></Field>
            <Field label="Longitudine"><Input data-testid="apt-lng" value={form.longitude || ""} onChange={(e) => set("longitude", e.target.value)} placeholder="9.1900" /></Field>
            <Field label="Link Tally documenti (immobile)" className="col-span-2"><Input data-testid="apt-tally" value={form.tally_documents_url || ""} onChange={(e) => set("tally_documents_url", e.target.value)} placeholder="https://tally.so/r/… (sovrascrive globale)" /></Field>
            <Field label="Link Welcome Guide (immobile)" className="col-span-2"><Input data-testid="apt-welcome" value={form.welcome_guide_url || ""} onChange={(e) => set("welcome_guide_url", e.target.value)} placeholder="https://…" /></Field>
            <Field label="Link Video (immobile)" className="col-span-2"><Input data-testid="apt-video" value={form.video_url || ""} onChange={(e) => set("video_url", e.target.value)} placeholder="https://…" /></Field>
            <Field label={t("imageUrl")} className="col-span-2"><Input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} /></Field>
            <Field label={t("owner")} className="col-span-2">
              <Select value={form.owner_id || "none"} onValueChange={(v) => set("owner_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="apt-owner"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("noOwner")}</SelectItem>
                  {owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t("notes")} className="col-span-2"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-apartment-btn" onClick={save} className="bg-primary hover:bg-primary/90">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

import { Field } from "../components/FormField";

